import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import clsx from 'clsx'
import { TrashIcon, XMarkIcon } from '@heroicons/react/24/outline'
import Card from '../ui/Card'
import Badge from '../ui/Badge'
import Button from '../ui/Button'
import PageHeader from '../layout/PageHeader'
import EditingReturnBanner, { backToLabelFor } from '../projects/EditingReturnBanner'
import { useFieldFocusHandler } from '../../hooks/useFieldFocusHandler'
import MultiSelect from '../ui/MultiSelect'
import SingleSelect from '../ui/SingleSelect'
import ZoomableImage from '../ui/ZoomableImage'
import { useProjects } from '../../contexts/ProjectsContext'
import { useWpsSections } from '../../contexts/WpsSectionsContext'
import { useCatalog } from '../../contexts/CatalogContext'
import { defaultTabBlockedMessage, resizeArrayByCount } from '../../utils/collectionSizing'
import {
  resolveBevelGeometrySlots,
  type ParameterLabelKey,
  type ResolvedBevelGeometry
} from '../../utils/bevelGeometryImagesClient'

export type GeometryParameterProfile = {
  jointType: string
  parameter1: string
  parameter2: string
  parameter3: string
  parameter4: string
  parameter5: string
  parameter6: string
  parameter7: string
  parameter8: string
  parameter9: string
  parameter10: string
  parameter11: string
  parameter12: string
  parameter13: string
  extraData: string
}

type BevelEntry = {
  id: string
  jointType: string[]
  backingType: string[]
  backingMaterial: string[]
  pipe1Id: string
  pipe2Id: string
  maxJointMisalignment: string
  geometryParameterProfiles?: GeometryParameterProfile[]
}

function emptyGeometryProfile(jointType: string): GeometryParameterProfile {
  return {
    jointType,
    parameter1: '',
    parameter2: '',
    parameter3: '',
    parameter4: '',
    parameter5: '',
    parameter6: '',
    parameter7: '',
    parameter8: '',
    parameter9: '',
    parameter10: '',
    parameter11: '',
    parameter12: '',
    parameter13: '',
    extraData: ''
  }
}

function syncGeometryProfiles(
  jointTypes: string[],
  prev: GeometryParameterProfile[] | undefined
): GeometryParameterProfile[] {
  const prevBy = new Map((prev ?? []).map((p) => [p.jointType, p]))
  return jointTypes.map((jt) => {
    const existing = prevBy.get(jt)
    const base = emptyGeometryProfile(jt)
    if (existing) return { ...base, ...existing, jointType: jt }
    return base
  })
}

function normalizeBackingMaterialSingle(arr: unknown): string[] {
  if (!Array.isArray(arr)) return []
  const strings = arr.filter((x): x is string => typeof x === 'string')
  const first = strings.find((s) => s.trim().length > 0)
  return first !== undefined ? [first] : []
}

function normalizeBevelRows(rows: unknown[]): BevelEntry[] {
  return rows.map((raw) => {
    const r = raw as BevelEntry
    const jointTypeRaw = Array.isArray(r.jointType)
      ? r.jointType.filter((x): x is string => typeof x === 'string' && String(x).trim().length > 0)
      : []
    const jointType = jointTypeRaw.length <= 1 ? jointTypeRaw : [jointTypeRaw[0]]
    return {
      id: String(r.id || `bevel-${Date.now()}`),
      jointType,
      backingType: Array.isArray(r.backingType) ? r.backingType : [],
      backingMaterial: normalizeBackingMaterialSingle(r.backingMaterial),
      pipe1Id: String(r.pipe1Id ?? ''),
      pipe2Id: String(r.pipe2Id ?? ''),
      maxJointMisalignment: String((r as { maxJointMisalignment?: unknown }).maxJointMisalignment ?? ''),
      geometryParameterProfiles: syncGeometryProfiles(jointType, r.geometryParameterProfiles)
    }
  })
}
export default function WpsBevelDetails() {
  const { id, pipeId } = useParams()
  const navigate = useNavigate()
  const { getProject } = useProjects()
  const { getSection, updateSection } = useWpsSections()
  const { getLabelsMerged, addLabel } = useCatalog()
  // Slice 06 of pqr-preview-field-navigation. When the user clicks the
  // Joint Preparation panel on the PQR Preview canvas they're routed
  // here with `?focusBevel=N&returnTo=…`. The focus handler +
  // back-button rewiring below reuse this value.
  const [searchParams] = useSearchParams()
  const returnTo = searchParams.get('returnTo')
  const project = id ? getProject(id) : undefined
  const pipeSet = project?.pipeSets.find((pipe) => pipe.id === pipeId)

  const [bevels, setBevels] = useState<BevelEntry[]>([])
  const [savedBevelsJson, setSavedBevelsJson] = useState<string>('[]')
  const [saving, setSaving] = useState(false)
  const bevelTabRefs = useRef<Record<number, HTMLButtonElement | null>>({})
  const [geometryModalBevelIndex, setGeometryModalBevelIndex] = useState<number | null>(null)
  const [geometryResolveLoading, setGeometryResolveLoading] = useState(false)
  const [geometryResolveResults, setGeometryResolveResults] = useState<ResolvedBevelGeometry[]>([])
  /** When set, the View geometry modal shows just this joint type (browse mode). */
  const [browseJointType, setBrowseJointType] = useState<string | null>(null)
  const [activeBevelTab, setActiveBevelTab] = useState(0)

  useFieldFocusHandler({
    ready: bevels.length > 0,
    onBeforeFocus: (params) => {
      if (params.focusBevel === null) return
      const idx = Number.parseInt(params.focusBevel, 10)
      if (!Number.isFinite(idx)) return
      if (idx >= 0 && idx < bevels.length) setActiveBevelTab(idx)
    },
    resolveTarget: (params) => {
      if (params.focusBevel === null) return null
      const idx = Number.parseInt(params.focusBevel, 10)
      if (!Number.isFinite(idx)) return null
      return bevelTabRefs.current[idx] ?? null
    }
  })
  const [bevelCountError, setBevelCountError] = useState<string | null>(null)
  const [bevelCountDraft, setBevelCountDraft] = useState('0')
  const bevelCountInputRef = useRef<HTMLInputElement>(null)

  const bevelOptionsList = getLabelsMerged(
    'bevel_joint_type',
    bevels.flatMap((b) => b.jointType)
  )
  const backingOptionsList = getLabelsMerged(
    'backing_requirement',
    bevels.flatMap((b) => b.backingType)
  )
  const backingMaterialOptions = getLabelsMerged(
    'backing_material',
    bevels.flatMap((b) => b.backingMaterial)
  )

  const pipeOptionsFromBaseMaterial = useMemo(() => {
    if (!pipeId) return []
    try {
      const parsed = getSection('base')
      if (!parsed) return []
      const lots = Array.isArray(parsed.manufacturerLots) ? parsed.manufacturerLots : []
      const options: { value: string; label: string }[] = []
      const labelCount = new Map<string, number>()
      lots.forEach((lot: { pipes?: Array<{ id: string; description?: string; heatNumber?: string }> }) => {
        const pipes = Array.isArray(lot.pipes) ? lot.pipes : []
        pipes.forEach((p: { id: string; description?: string; heatNumber?: string }) => {
          const desc = (p.description || '').trim()
          const heat = (p.heatNumber || '').trim()
          let label = desc && heat ? `${desc} (${heat})` : desc || heat || `Pipe ${p.id.slice(-6)}`
          const n = (labelCount.get(label) ?? 0) + 1
          labelCount.set(label, n)
          if (n > 1) label = `${label} – ${n}`
          options.push({ value: p.id, label })
        })
      })
      return options
    } catch {
      return []
    }
  }, [pipeId, getSection])

  useEffect(() => {
    if (!pipeId) return
    const parsed = getSection('bevel')
    if (parsed) {
      try {
        if (Array.isArray(parsed.bevels) && parsed.bevels.length > 0) {
          const next = normalizeBevelRows(parsed.bevels)
          setBevels(next)
          setSavedBevelsJson(JSON.stringify(next))
          setBevelCountDraft(String(next.length))
          return
        }
        const jointType = Array.isArray(parsed.jointType) ? parsed.jointType.filter((x: unknown) => typeof x === 'string') : []
        const next: BevelEntry[] = [
          {
            id: `bevel-${Date.now()}`,
            jointType,
            backingType: parsed.backingType || [],
            backingMaterial: normalizeBackingMaterialSingle(parsed.backingMaterial),
            pipe1Id: '',
            pipe2Id: '',
            maxJointMisalignment: '',
            geometryParameterProfiles: syncGeometryProfiles(jointType, undefined)
          }
        ]
        setBevels(next)
        setSavedBevelsJson(JSON.stringify(next))
        setBevelCountDraft(String(next.length))
        return
      } catch (error) {
        // ignore
      }
    }
    if (pipeSet) {
      const next: BevelEntry[] = [
        {
          id: `bevel-${Date.now()}`,
          jointType: [],
          backingType: [],
          backingMaterial: [],
          pipe1Id: '',
          pipe2Id: '',
          maxJointMisalignment: '',
          geometryParameterProfiles: []
        }
      ]
      setBevels(next)
      setSavedBevelsJson(JSON.stringify(next))
      setBevelCountDraft('1')
    }
  }, [pipeId, pipeSet, getSection])

  useEffect(() => {
    let cancelled = false
    if (geometryModalBevelIndex === null) {
      setGeometryResolveResults([])
      setGeometryResolveLoading(false)
      return
    }
    const bevel = bevels[geometryModalBevelIndex]
    if (!bevel) {
      setGeometryResolveResults([])
      setGeometryResolveLoading(false)
      return
    }
    // Browse mode (user picked a joint type from the modal) takes precedence over the
    // currently-selected joint type on the form. Falls back to the form selection when
    // no browse type is set.
    const jointTypes =
      browseJointType && browseJointType.trim() ? [browseJointType.trim()] : bevel.jointType
    if (jointTypes.length === 0) {
      setGeometryResolveResults([])
      setGeometryResolveLoading(false)
      return
    }
    setGeometryResolveLoading(true)
    const slots = jointTypes.map((jt) => {
      const prof =
        bevel.geometryParameterProfiles?.find((p) => p.jointType === jt) ?? emptyGeometryProfile(jt)
      return {
        jointType: jt,
        parameter1: prof.parameter1,
        parameter2: prof.parameter2,
        parameter3: prof.parameter3,
        parameter4: prof.parameter4,
        parameter5: prof.parameter5,
        parameter6: prof.parameter6,
        parameter7: prof.parameter7,
        parameter8: prof.parameter8,
        parameter9: prof.parameter9,
        parameter10: prof.parameter10,
        parameter11: prof.parameter11,
        parameter12: prof.parameter12,
        parameter13: prof.parameter13,
        extraData: prof.extraData ?? ''
      }
    })
    void resolveBevelGeometrySlots(slots)
      .then((res) => {
        if (cancelled) return
        setGeometryResolveResults(res.results)
      })
      .catch(() => {
        if (cancelled) return
        setGeometryResolveResults(
          slots.map((s) => ({
            jointType: s.jointType,
            imageUrl: null,
            parameterLabels: null,
            parameterValues: null
          }))
        )
      })
      .finally(() => {
        if (!cancelled) setGeometryResolveLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [geometryModalBevelIndex, bevels, browseJointType])

  const isDirty = useMemo(() => JSON.stringify(bevels) !== savedBevelsJson, [bevels, savedBevelsJson])

  useEffect(() => {
    const onPointerDown = (e: PointerEvent) => {
      if (!bevelCountError) return
      if (bevelCountInputRef.current?.contains(e.target as Node)) return
      setBevelCountError(null)
      setBevelCountDraft(String(bevels.length))
    }
    document.addEventListener('pointerdown', onPointerDown, true)
    return () => document.removeEventListener('pointerdown', onPointerDown, true)
  }, [bevelCountError, bevels.length])

  const handleSave = async () => {
    if (!pipeId || !isDirty || saving) return
    setSaving(true)
    try {
      await updateSection('bevel', { bevels })
      setSavedBevelsJson(JSON.stringify(bevels))
      toast.success('Bevel details saved')
      navigate(-1)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not save bevel details')
    } finally {
      setSaving(false)
    }
  }

  const bevelEntryOccupied = (b: BevelEntry): boolean =>
    b.jointType.length > 0 ||
    b.backingType.length > 0 ||
    b.backingMaterial.length > 0 ||
    Boolean(b.pipe1Id?.trim()) ||
    Boolean(b.pipe2Id?.trim()) ||
    Boolean(b.maxJointMisalignment?.trim())

  const applyBevelCount = (n: number) => {
    const count = Math.max(0, n)
    setBevelCountError(null)
    setBevels((prev) => {
      const empty = (): BevelEntry => ({
        id: `bevel-${Date.now()}-${Math.random().toString(16).slice(2, 6)}`,
        jointType: [],
        backingType: [],
        backingMaterial: [],
        pipe1Id: '',
        pipe2Id: '',
        maxJointMisalignment: '',
        geometryParameterProfiles: []
      })
      const res = resizeArrayByCount(prev, count, empty, (b) => bevelEntryOccupied(b), {
        blockedShrinkMessage: defaultTabBlockedMessage
      })
      if (!res.ok) {
        setBevelCountError(res.error)
        return prev
      }
      if (res.items.length !== prev.length && res.items.length < prev.length) {
        setActiveBevelTab((current) =>
          current >= res.items.length ? Math.max(0, res.items.length - 1) : current
        )
      }
      return res.items
    })
  }

  useEffect(() => {
    if (bevelCountError) return
    setBevelCountDraft(String(bevels.length))
  }, [bevels.length, bevelCountError])

  const deleteBevelAt = (index: number) => {
    setBevels((prev) => {
      if (prev.length <= 1) return prev
      const next = prev.filter((_, i) => i !== index)
      setActiveBevelTab((cur) => {
        if (cur >= next.length) return Math.max(0, next.length - 1)
        if (cur > index) return cur - 1
        return cur
      })
      return next
    })
    setBevelCountError(null)
  }

  const pipeOptionValues = useMemo(() => pipeOptionsFromBaseMaterial.map((o) => o.label), [pipeOptionsFromBaseMaterial])
  const pipeValueToLabel = (value: string) =>
    (pipeOptionsFromBaseMaterial.find((o) => o.value === value)?.label ?? value) || ''

  if (!project || !pipeSet) {
    return (
      <Card title="Bevel Details not found">
        <p className="text-sm text-gray-500">Return to the Project Hub to continue.</p>
        <div className="mt-4">
          <Link to="/resources">
            <Button>Go to Project Hub</Button>
          </Link>
        </div>
      </Card>
    )
  }

  const pageHeaderBackTo = returnTo
    ? { to: returnTo, label: backToLabelFor(returnTo) }
    : { onClick: () => navigate(-1), label: 'Back' }

  return (
    <div className="space-y-6">
      <EditingReturnBanner returnTo={returnTo} contextLabel="Bevel Details" />
      <PageHeader
        backTo={pageHeaderBackTo}
        title={
          <>
            <span>Bevel Details</span>
            <span className="text-heritage-grey/60">•</span>
            <span className="truncate text-text-grey">{project.name}</span>
          </>
        }
        subtitle={
          <>
            <span>{(project.endClients?.[0] ?? project.client)} • {project.location}</span>
            <Badge tone="primary">
              {pipeSet.nominalOd} {pipeSet.nominalOdUnit} × {pipeSet.wallThickness}{' '}
              {pipeSet.wallThicknessUnit}
            </Badge>
          </>
        }
        actions={
          <>
            <Button disabled={!isDirty || saving} loading={saving} onClick={() => void handleSave()}>Save</Button>
          </>
        }
      />

      <div className="space-y-6">
      <div className="min-w-0 rounded-lg border border-gray-200 bg-white p-4">
        <label className="text-xs font-medium text-gray-500">Number of bevels planned</label>
        <input
          ref={bevelCountInputRef}
          type="number"
          min={0}
          className="input-primary mt-1 w-24"
          value={bevelCountDraft}
          onChange={(e) => {
            const raw = e.target.value
            setBevelCountDraft(raw)
            if (raw.trim() === '') return
            const n = Math.max(0, parseInt(raw, 10) || 0)
            applyBevelCount(n)
          }}
          onBlur={() => {
            setBevelCountError(null)
            setBevelCountDraft(String(bevels.length))
          }}
        />
        {bevelCountError && <p className="mt-2 text-xs text-error-600">{bevelCountError}</p>}
      </div>

      {bevels.length > 0 && (
        <div className="rounded-lg border border-gray-200 bg-white">
          <div className="flex border-b border-gray-200">
            {bevels.map((_, index) => (
              <div
                key={bevels[index].id}
                className={clsx(
                  'flex min-w-0 flex-1 items-stretch border-b-2',
                  activeBevelTab === index ? 'border-primary-600' : 'border-transparent'
                )}
              >
                <button
                  type="button"
                  ref={(el) => {
                    bevelTabRefs.current[index] = el
                  }}
                  data-focus-bevel={index}
                  onClick={() => setActiveBevelTab(index)}
                  className={clsx(
                    'min-w-0 flex-1 px-3 py-3 text-left text-sm font-medium transition-colors',
                    activeBevelTab === index ? 'text-primary-600' : 'text-gray-500 hover:text-gray-700'
                  )}
                >
                  Bevel {index + 1}
                </button>
                {bevels.length > 1 && (
                  <button
                    type="button"
                    title="Delete this bevel"
                    className="shrink-0 px-2 text-gray-400 hover:text-error-600"
                    onClick={(e) => {
                      e.stopPropagation()
                      deleteBevelAt(index)
                    }}
                    aria-label={`Delete bevel ${index + 1}`}
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
          {bevels[activeBevelTab] && (
            <div className="flex justify-end border-b border-gray-100 px-4 py-2">
              <Button
                type="button"
                variant="secondary"
                className="text-xs"
                onClick={() => {
                  setBrowseJointType(null)
                  setGeometryModalBevelIndex(activeBevelTab)
                }}
              >
                View geometry
              </Button>
            </div>
          )}
          <div className="min-w-0 p-0">
          <div className="p-4">
            {(() => {
              const bevel = bevels[activeBevelTab]
              if (!bevel) return null
              return (
                <>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <label className="text-xs font-medium text-gray-500">Pipe combination – Side 1</label>
                      <SingleSelect
                        value={pipeValueToLabel(bevel.pipe1Id)}
                        options={pipeOptionValues}
                        placeholder={pipeOptionsFromBaseMaterial.length === 0 ? 'Add pipes in Base Material first' : 'Select pipe...'}
                        className="mt-1"
                        onChange={(selectedLabel) => {
                          const id = pipeOptionsFromBaseMaterial.find((o) => o.label === selectedLabel)?.value ?? ''
                          setBevels((prev) =>
                            prev.map((b) => (b.id === bevel.id ? { ...b, pipe1Id: id } : b))
                          )
                        }}
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500">Pipe combination – Side 2</label>
                      <SingleSelect
                        value={pipeValueToLabel(bevel.pipe2Id)}
                        options={pipeOptionValues}
                        placeholder={pipeOptionsFromBaseMaterial.length === 0 ? 'Add pipes in Base Material first' : 'Select pipe...'}
                        className="mt-1"
                        onChange={(selectedLabel) => {
                          const id = pipeOptionsFromBaseMaterial.find((o) => o.label === selectedLabel)?.value ?? ''
                          setBevels((prev) =>
                            prev.map((b) => (b.id === bevel.id ? { ...b, pipe2Id: id } : b))
                          )
                        }}
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500">Bevel Type</label>
                      <SingleSelect
                        value={bevel.jointType[0] ?? ''}
                        options={bevelOptionsList}
                        placeholder="Select bevel type..."
                        addLabel="Add Bevel Type"
                        onChange={(value) => {
                          const jointType = value.trim() ? [value] : []
                          setBevels((prev) =>
                            prev.map((b) =>
                              b.id === bevel.id
                                ? {
                                    ...b,
                                    jointType,
                                    geometryParameterProfiles: syncGeometryProfiles(jointType, b.geometryParameterProfiles)
                                  }
                                : b
                            )
                          )
                        }}
                        onCreateOption={async (value) => {
                          await addLabel('bevel_joint_type', value)
                          setBevels((prev) =>
                            prev.map((b) =>
                              b.id === bevel.id
                                ? {
                                    ...b,
                                    jointType: [value],
                                    geometryParameterProfiles: syncGeometryProfiles([value], b.geometryParameterProfiles)
                                  }
                                : b
                            )
                          )
                        }}
                        className="mt-1"
                      />
                    </div>
                    {/* The per-bevel "Geometry parameters" inline grid (P1..P13 inputs) was
                        removed by request: parameter names AND values are now authored only in
                        Settings → Bevel Geometry Images, and surfaced read-only via the
                        "View geometry" modal on this page. The bevel still stores joint type
                        and backing data; the resolver pulls the matching admin row's
                        parameter values + labels for display + PQR PDF rendering. */}
                    <div>
                      <label className="text-xs font-medium text-gray-500">Backing Requirement</label>
                      <MultiSelect
                        values={bevel.backingType}
                        options={backingOptionsList}
                        placeholder="Select backing requirement..."
                        addLabel="Add Backing Requirement"
                        onChange={(values) =>
                          setBevels((prev) =>
                            prev.map((b) =>
                              b.id === bevel.id ? { ...b, backingType: values } : b
                            )
                          )
                        }
                        onCreateOption={async (value) => {
                          await addLabel('backing_requirement', value)
                          setBevels((prev) =>
                            prev.map((b) =>
                              b.id === bevel.id
                                ? { ...b, backingType: b.backingType.includes(value) ? b.backingType : [...b.backingType, value] }
                                : b
                            )
                          )
                        }}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500">Backing Material</label>
                      <MultiSelect
                        values={bevel.backingMaterial}
                        options={backingMaterialOptions}
                        placeholder="Select backing material..."
                        addLabel="Add Backing Material"
                        maxSelections={1}
                        onChange={(values) =>
                          setBevels((prev) =>
                            prev.map((b) =>
                              b.id === bevel.id ? { ...b, backingMaterial: values } : b
                            )
                          )
                        }
                        onCreateOption={async (value) => {
                          await addLabel('backing_material', value)
                          setBevels((prev) =>
                            prev.map((b) =>
                              b.id === bevel.id ? { ...b, backingMaterial: [value] } : b
                            )
                          )
                        }}
                        className="mt-1"
                      />
                    </div>
                   
                  </div>
                </>
              )
            })()}
          </div>
          </div>
        </div>
      )}
      </div>

      {geometryModalBevelIndex !== null && bevels[geometryModalBevelIndex] && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="geometry-modal-title"
          onClick={() => {
            setGeometryModalBevelIndex(null)
            setBrowseJointType(null)
          }}
        >
          <div
            className="max-h-[90vh] w-full max-w-5xl overflow-auto rounded-lg border border-gray-200 bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 flex items-center justify-between border-b border-gray-200 bg-white px-4 py-3">
              <h2 id="geometry-modal-title" className="text-sm font-semibold text-gray-900">
                Bevel {geometryModalBevelIndex + 1} – Geometry
              </h2>
              <button
                type="button"
                className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                onClick={() => {
                  setGeometryModalBevelIndex(null)
                  setBrowseJointType(null)
                }}
                aria-label="Close"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-4 p-4">
              {(() => {
                const bevel = bevels[geometryModalBevelIndex]
                const formJointType = bevel.jointType[0] ?? ''
                const activeJointType = browseJointType ?? formJointType
                return (
                  <>
                    <div className="rounded-lg border border-gray-200 bg-gray-50/70 p-3">
                      <label className="text-xs font-medium text-gray-700">Browse bevel type</label>
                      <p className="mt-0.5 text-[11px] text-gray-500">
                        Pick any bevel type from the catalog (numbered types are renameable in
                        Settings → Dropdown Catalog → bevel_joint_type) to preview its diagram with
                        the parameters saved on this bevel.
                      </p>
                      <SingleSelect
                        value={activeJointType}
                        options={bevelOptionsList}
                        placeholder="Select a bevel type to preview..."
                        addLabel="Add Bevel Type"
                        className="mt-2"
                        onChange={(v) => setBrowseJointType(v)}
                        onCreateOption={async (v) => {
                          await addLabel('bevel_joint_type', v)
                          setBrowseJointType(v)
                        }}
                      />
                      {browseJointType && browseJointType !== formJointType && (
                        <div className="mt-2 flex items-center justify-between gap-2 text-[11px]">
                          <span className="text-amber-700">
                            Previewing "{browseJointType}". Form selection: {formJointType || '—'}.
                          </span>
                          <button
                            type="button"
                            className="rounded border border-gray-300 bg-white px-2 py-0.5 text-[11px] font-medium text-gray-700 hover:bg-gray-50"
                            onClick={() => setBrowseJointType(null)}
                          >
                            Reset to form
                          </button>
                        </div>
                      )}
                    </div>
                    {geometryResolveLoading && (
                      <p className="text-xs text-gray-500">Loading diagram…</p>
                    )}
                    <div className="grid grid-cols-1 gap-4">
                      {(activeJointType ? [activeJointType] : []).map((jt, slotIdx) => {
                        const resolved = geometryResolveResults[slotIdx]
                        const imageUrl = resolved?.imageUrl ?? null
                        const labels = resolved?.parameterLabels ?? null
                        const adminValues = resolved?.parameterValues ?? null
                        const specRows: Array<{ label: string; value: string }> = []
                        for (let i = 1; i <= 13; i += 1) {
                          const key = `parameter${i}` as ParameterLabelKey
                          const labelText = labels?.[key]
                          const value = adminValues?.[key]
                          // Skip slots where the admin uploaded neither a label nor a value
                          // for this parameter — keeps the spec list focused on what the
                          // admin actually populated.
                          if (!labelText && !value) continue
                          specRows.push({
                            label: labelText ?? `Parameter ${i}`,
                            value: value && value.trim() ? value : '—'
                          })
                        }
                        return (
                          <div
                            key={`${jt}-${slotIdx}`}
                            data-testid={`geometry-preview-${slotIdx}`}
                            className="grid gap-3 rounded-lg border border-gray-200 bg-white p-3 md:grid-cols-2"
                          >
                            <div className="space-y-2">
                              <p className="text-xs font-semibold text-gray-900">{jt}</p>
                              <div className="rounded-md border border-gray-100 bg-gray-50 p-2">
                                <p className="text-[10px] font-medium uppercase tracking-wide text-gray-500">
                                  Reference specs
                                </p>
                                <div className="mt-2 max-h-72 space-y-1.5 overflow-y-auto text-[11px] text-gray-600">
                                  {specRows.length === 0 ? (
                                    <p className="text-[11px] text-gray-500">
                                      No labelled parameters yet. Admin can label P1–P13 in
                                      Settings → Bevel Geometry Images.
                                    </p>
                                  ) : (
                                    specRows.map((row) => (
                                      <div
                                        key={row.label}
                                        className="flex items-start justify-between gap-2 border-b border-gray-100 pb-1 last:border-0"
                                      >
                                        <span className="text-gray-500">{row.label}</span>
                                        <span className="shrink-0 font-medium text-gray-800">
                                          {row.value}
                                        </span>
                                      </div>
                                    ))
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex min-h-[280px] flex-col rounded-lg border border-dashed border-gray-200 bg-gray-50 p-2">
                              {imageUrl ? (
                                <ZoomableImage
                                  src={imageUrl}
                                  alt={`${jt} geometry`}
                                  className="h-72 w-full"
                                />
                              ) : (
                                <p className="m-auto px-2 text-center text-xs text-gray-500">
                                  {geometryResolveLoading
                                    ? '…'
                                    : 'No diagram uploaded for this type and parameter combination.'}
                                </p>
                              )}
                            </div>
                          </div>
                        )
                      })}
                      {!activeJointType && (
                        <p className="rounded-lg border border-dashed border-gray-200 p-6 text-center text-xs text-gray-500">
                          Pick a bevel type above to preview its diagram.
                        </p>
                      )}
                    </div>
                  </>
                )
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
